import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { Phone, MapPin, CheckCircle, ArrowRight, Shield, Clock, Award, Building2, TreePine, AlertTriangle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileCTA from "@/components/MobileCTA";
import StickyCtaBar from "@/components/StickyCtaBar";
import neighborhoodPages from "@/data/neighborhoodPages";

const typeIcons = { luxury: Shield, storm: AlertTriangle, commercial: Building2 };
const typeLabels = { luxury: "Luxury Community", storm: "Storm Recovery Zone", commercial: "Commercial Service" };
const typeColors = { luxury: "text-amber-400", storm: "text-red-400", commercial: "text-blue-400" };

const NeighborhoodPage = ({ slug }) => {
  const page = neighborhoodPages.find((p) => p.slug === slug);
  if (!page) return null;

  const { name, parentArea, type, metaTitle, metaDescription, heroTitle, heroSubtitle, coords, content } = page;
  const TypeIcon = typeIcons[type];

  // Get related pages (same parentArea or same type, excluding current)
  const related = neighborhoodPages
    .filter((p) => p.slug !== slug && (p.parentArea === parentArea || p.type === type))
    .slice(0, 6);

  // Main service area link
  const parentSlugMap = {
    "Oro Valley": "/oro-valley-stump-grinding",
    "Tucson": "/tucson-stump-grinding",
    "Marana": "/tucson-stump-grinding",
    "Sahuarita": "/sahuarita-stump-grinding",
    "Oracle": "/oracle-stump-grinding",
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://tucsonstumpgrinding.com/" + slug + "#localbusiness",
    "name": "Tucson Stump Grinding - " + name,
    "description": metaDescription,
    "url": "https://tucsonstumpgrinding.com/" + slug,
    "telephone": "+1-520-200-7739",
    "priceRange": "$300-$850",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": parentArea === "Marana" ? "Marana" : parentArea === "Sahuarita" ? "Sahuarita" : parentArea === "Oro Valley" ? "Oro Valley" : parentArea === "Oracle" ? "Oracle" : "Tucson",
      "addressRegion": "AZ",
      "addressCountry": "US"
    },
    "geo": { "@type": "GeoCoordinates", "latitude": coords.lat, "longitude": coords.lng },
    "areaServed": { "@type": "City", "name": parentArea === "Marana" ? "Marana" : parentArea === "Sahuarita" ? "Sahuarita" : parentArea === "Oro Valley" ? "Oro Valley" : parentArea === "Oracle" ? "Oracle" : "Tucson" },
    "serviceType": ["Stump Grinding", "Tree Stump Removal", "Root Grinding", type === "commercial" ? "Commercial Stump Removal" : "Residential Stump Grinding"],
    "paymentAccepted": ["Cash", "Credit Card", "Visa", "Mastercard", "American Express", "Discover"],
    "priceRange": "$300-$850",
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "127" },
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "07:00", "closes": "18:00" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "08:00", "closes": "14:00" }
    ]
  };

  return <NeighborhoodPageInner
    slug={slug} page={page} name={name} parentArea={parentArea} type={type}
    metaTitle={metaTitle} metaDescription={metaDescription} heroTitle={heroTitle}
    heroSubtitle={heroSubtitle} content={content} TypeIcon={TypeIcon}
    related={related} parentSlugMap={parentSlugMap} schemaData={schemaData}
  />;
};

const NeighborhoodPageInner = ({ slug, page, name, parentArea, type, metaTitle, metaDescription, heroTitle, heroSubtitle, content, TypeIcon, related, parentSlugMap, schemaData }) => {
  useSEO({
    title: metaTitle,
    canonical: "https://tucsonstumpgrinding.com/" + slug,
    description: metaDescription,
    ogTitle: metaTitle,
    ogDescription: metaDescription,
    ogUrl: "https://tucsonstumpgrinding.com/" + slug,
  });

  useEffect(() => {
    const existing = document.getElementById("schema-" + slug);
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-" + slug;
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);
    return () => { const el = document.getElementById("schema-" + slug); if (el) el.remove(); };
  }, [slug, schemaData]);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-[#1A1A1A] pt-32 pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <TypeIcon className={"w-5 h-5 " + typeColors[type]} />
            <span className={"font-heading text-xs font-bold uppercase tracking-widest " + typeColors[type]}>
              {typeLabels[type]} — {parentArea}
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white uppercase mb-4 leading-tight">
            {heroTitle}
          </h1>
          <p className="font-body text-lg text-gray-300 max-w-3xl mb-8">{heroSubtitle}</p>
          <div className="flex flex-wrap gap-4">
            <a href="tel:+15202007739,2" className="inline-flex items-center gap-2 bg-[#FF4D00] hover:bg-[#e64400] text-white px-6 py-3 font-heading font-bold uppercase tracking-wider transition-colors" data-testid="hero-call-btn">
              <Phone className="w-5 h-5" /> Call (520) 200-7739 Ext 2
            </a>
            <Link to="/#quote-form" className="inline-flex items-center gap-2 border-2 border-white text-white hover:bg-white hover:text-[#1A1A1A] px-6 py-3 font-heading font-bold uppercase tracking-wider transition-colors" data-testid="hero-quote-btn">
              Get Free Quote <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {content.map((section, i) => (
            <div key={i} className="mb-10">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A1A1A] uppercase mb-4">{section.heading}</h2>
              {section.paragraphs.map((p, j) => (
                <p key={j} className="font-body text-gray-700 leading-relaxed mb-4">{p}</p>
              ))}
            </div>
          ))}

          {/* Trust Badges */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12 pt-8 border-t-2 border-gray-200">
            {[
              { icon: Shield, label: "Licensed & Insured" },
              { icon: Clock, label: "Same-Day Available" },
              { icon: Award, label: "10+ Years Experience" },
              { icon: TreePine, label: "$300-$850 Pricing" },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-2">
                <b.icon className="w-5 h-5 text-[#FF4D00] flex-shrink-0" />
                <span className="font-body text-sm font-semibold text-[#1A1A1A]">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-[#FF4D00] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white uppercase mb-4">
            Need Stump Grinding in {name}?
          </h2>
          <p className="font-body text-white/90 mb-6">
            Free on-site estimates. No obligation. Financing available.
          </p>
          <a href="tel:+15202007739,2" className="inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-black text-white px-8 py-4 font-heading font-bold uppercase tracking-wider transition-colors" data-testid="cta-call-btn">
            <Phone className="w-5 h-5" /> Call Now — (520) 200-7739 Ext 2
          </a>
        </div>
      </section>

      {/* Related Areas */}
      <section className="py-16 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-bold text-[#1A1A1A] uppercase mb-8">
            We Also Serve These Areas
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {related.map((r) => (
              <Link key={r.slug} to={"/" + r.slug} className="flex items-center gap-3 bg-white p-4 border-2 border-gray-200 hover:border-[#FF4D00] transition-colors group" data-testid={"related-" + r.slug}>
                <MapPin className="w-5 h-5 text-[#FF4D00] flex-shrink-0" />
                <div>
                  <span className="font-heading text-sm font-bold text-[#1A1A1A] group-hover:text-[#FF4D00] uppercase">{r.name}</span>
                  <span className="block font-body text-xs text-gray-500">{r.parentArea}</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to={parentSlugMap[parentArea] || "/tucson-stump-grinding"} className="inline-flex items-center gap-2 text-[#FF4D00] font-heading font-bold uppercase text-sm hover:underline">
              <ArrowRight className="w-4 h-4" /> View All {parentArea} Services
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 text-gray-600 font-heading font-bold uppercase text-sm hover:underline">
              <ArrowRight className="w-4 h-4" /> Back to Home
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <MobileCTA />
      <StickyCtaBar />
    </div>
  );
};

export default NeighborhoodPage;
